import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { listWards, listRooms, listBeds } from '../../services/api/bedInventoryApi';
import { listBedCategories } from '../../services/api/bedCategoryApi';
import { useBedStates } from './hooks';
import {
  BED_STATES,
  BED_STATE_LABELS,
  isBedSelectable,
} from '../../constants/admission';
import { formatRupees } from './estimateModel';

const WARDS_PER_GROUP = 6; // long-list: show first N per group, "+n more" expands

/**
 * WardBedPicker (S3) — ward → room → bed picker for the admit flow + the desk
 * assign-bed drawer. BINDING B5/B6 rule-(a): the picker ANCHORS on the
 * ADMISSION's department (prop `anchorDeptId`, NEVER the viewer's) but never
 * RESTRICTS by it — wards tagged to the anchor group first ("this admission's
 * department"), everything else falls under "All other wards" and is never
 * removed. Absent anchor → single flat full list.
 *
 * Occupied / cleaning / blocked beds are shown DISABLED with a status chip
 * (Business Q3 — not hidden). Absent BEDSTATE ≡ VACANT → selectable. Empty-set
 * carve-out both directions (no beds → empty-state; a selected bed whose ward is
 * now outside the anchor group stays visible).
 *
 * Data: one fetch each of wards/rooms/beds/categories on mount (bounded — no
 * per-render growth); bed availability via useBedStates (single-flight).
 *
 * Controlled: `value` = selected bed_id | null; `onChange(bedObj|null)` where
 * bedObj = { bed_id, label, ward_id, room_id, bedCategory }.
 *
 * Round-2 B1 — occupant on the chip: a non-vacant chip renders WHO holds the
 * bed (`Occupied by <name> · <IP>`, from `useBedStates`'s `occupantOf`)
 * instead of a bare status enum, so the desk never blindly tries to move a
 * patient onto an occupied bed. `currentBedId` (optional — only the
 * change-bed flow has one; the assign-bed/admit flows are bed-less and pass
 * nothing) marks THIS admission's own current bed "Current bed" instead of
 * "Occupied by …" — still inert, never selectable.
 */
export default function WardBedPicker({ anchorDeptId, value, onChange, disabled, currentBedId = null }) {
  const [wards, setWards] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [categoriesById, setCategoriesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedWardId, setSelectedWardId] = useState(null);
  const [wardSearch, setWardSearch] = useState('');
  const [bedSearch, setBedSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [collapsedRooms, setCollapsedRooms] = useState({});

  const { statusOf, occupantOf } = useBedStates();

  // Retry: clear the error, re-arm loading, and bump the key so both fetch
  // effects re-run. Used by the visible error's "Retry" button so a failed
  // load (e.g. a 403 before the read-permission broadens) never hangs on the
  // "Loading wards…" spinner forever.
  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    setReloadKey((k) => k + 1);
  }, []);

  // Fetch inventory on mount / after a Retry (bounded: 3 calls per run; re-anchoring
  // re-runs the ward group only, since inventory is org-wide). No ref once-guard:
  // under React 18 StrictMode the effect double-invokes in dev (mount→cleanup→mount),
  // and a once-guard would let invoke #2 early-return while invoke #1's cleanup has
  // already set alive=false — the resolving fetch then skips setLoading(false) and
  // the spinner hangs forever. Instead we let the effect run per its [reloadKey]
  // deps: the surviving (mounted) invoke owns a fresh `alive` and always reaches
  // setLoading(false). The dev double-fetch is bounded (prod runs once); `alive`
  // only guards setState-after-unmount, never gates loading off.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [rRes, bRes, cRes] = await Promise.all([
          listRooms(),
          listBeds(),
          listBedCategories(),
        ]);
        if (!alive) return;
        if (!rRes.success || !bRes.success || !cRes.success) {
          setError(rRes.error || bRes.error || cRes.error || 'Could not load bed inventory.');
          setLoading(false);
          return;
        }
        setRooms(rRes.data.rooms);
        setBeds(bRes.data.beds);
        const map = {};
        for (const c of cRes.data.bed_categories || []) {
          if (c && c.bed_category_id) map[c.bed_category_id] = c;
        }
        setCategoriesById(map);
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setError('Could not load bed inventory.');
        setLoading(false);
      }
    })();
    return () => { alive = false; };
    // anchorDeptId intentionally NOT in deps: inventory is org-wide; re-anchoring
    // only re-groups client-side (below). reloadKey re-runs it after a Retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  // Re-anchoring: refetch the (grouped) ward list when the anchor changes so the
  // union grouping follows the new dept. Bounded (one call per anchor change).
  // try/catch + `success === false` → visible error + Retry (never a hung spinner).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const wRes = await listWards({ department_id: anchorDeptId || undefined });
        if (!alive) return;
        if (!wRes.success) {
          setError(wRes.error || 'Could not load wards.');
          setLoading(false);
          return;
        }
        setWards(wRes.data.wards);
      } catch (e) {
        if (alive) {
          setError('Could not load wards.');
          setLoading(false);
        }
      }
    })();
    return () => { alive = false; };
  }, [anchorDeptId, reloadKey]);

  const roomById = useMemo(() => {
    const m = {};
    for (const r of rooms) m[r.room_id] = r;
    return m;
  }, [rooms]);

  const categoryOfBed = useMemo(
    () => (bed) => {
      const room = roomById[bed.room_id];
      const catId = room && room.bedcat_id;
      return catId ? categoriesById[catId] : null;
    },
    [roomById, categoriesById],
  );

  // ── Ward grouping (never-exclude union) ────────────────────────────────
  const { anchorWards, otherWards } = useMemo(() => {
    const q = wardSearch.trim().toLowerCase();
    const match = (w) => !q || (w.name || '').toLowerCase().includes(q);
    const anchor = [];
    const other = [];
    const seen = new Set(); // dedupe ward by id (multi-dept ward appears once)
    for (const w of wards) {
      if (seen.has(w.ward_id)) continue;
      seen.add(w.ward_id);
      if (!match(w)) continue;
      const inAnchor = anchorDeptId && (w.department_ids || []).includes(anchorDeptId);
      (inAnchor ? anchor : other).push(w);
    }
    return { anchorWards: anchor, otherWards: other };
  }, [wards, anchorDeptId, wardSearch]);

  const bedsOfWard = useMemo(() => {
    if (!selectedWardId) return [];
    const q = bedSearch.trim().toLowerCase();
    return beds
      .filter((b) => b.ward_id === selectedWardId)
      .filter((b) => !q || (b.label || '').toLowerCase().includes(q));
  }, [beds, selectedWardId, bedSearch]);

  const bedsByRoom = useMemo(() => {
    const groups = {};
    for (const b of bedsOfWard) {
      (groups[b.room_id] = groups[b.room_id] || []).push(b);
    }
    return groups;
  }, [bedsOfWard]);

  const selectWard = (wardId) => {
    setSelectedWardId(wardId);
    setBedSearch('');
    // ward change CLEARS bed selection (S1 interaction table).
    if (value) onChange(null);
  };

  const selectBed = (bed) => {
    if (disabled) return;
    onChange({
      bed_id: bed.bed_id,
      label: bed.label,
      ward_id: bed.ward_id,
      room_id: bed.room_id,
      bedCategory: categoryOfBed(bed),
    });
  };

  const cardCls = 'rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden';
  const chip = (text, tone) => (
    <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${tone}`}>{text}</span>
  );

  if (error) {
    return (
      <div
        className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-3"
        data-testid="picker-error"
      >
        <div className="mb-2">{error}</div>
        <button
          type="button"
          onClick={retry}
          className="px-3 py-1.5 rounded-md border border-red-400 dark:border-red-600 bg-white dark:bg-slate-800 text-red-700 dark:text-red-200 text-[12.5px] font-semibold hover:bg-red-100 dark:hover:bg-slate-700"
          data-testid="picker-retry"
        >
          Retry
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="text-sm text-slate-500 dark:text-slate-400" data-testid="picker-loading">Loading wards…</div>;
  }

  const renderWardGroup = (label, groupWards, key) => {
    if (groupWards.length === 0) return null;
    const expanded = expandedGroups[key];
    const shown = expanded ? groupWards : groupWards.slice(0, WARDS_PER_GROUP);
    const rest = groupWards.length - shown.length;
    return (
      <div className="mb-3" key={key}>
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">
          {label} ({groupWards.length})
        </div>
        <div className="flex flex-wrap gap-2">
          {shown.map((w) => (
            <button
              key={w.ward_id}
              type="button"
              onClick={() => selectWard(w.ward_id)}
              className={`px-3 py-1.5 rounded-lg border text-[12.5px] ${
                selectedWardId === w.ward_id
                  ? 'bg-teal-600 border-teal-600 text-white'
                  : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              {w.name}
            </button>
          ))}
          {rest > 0 && (
            <button
              type="button"
              onClick={() => setExpandedGroups((s) => ({ ...s, [key]: true }))}
              className="px-3 py-1.5 rounded-lg border border-dashed border-slate-400 dark:border-slate-500 bg-slate-50 dark:bg-slate-800 text-teal-700 dark:text-teal-300 text-[12.5px] font-semibold"
            >
              +{rest} more
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="ward-bed-picker">
      {/* Ward picker */}
      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
        Ward — grouped by the admission&rsquo;s department; nothing is excluded
      </label>
      <input
        className="w-full mb-3 px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        placeholder={`Search ${wards.length} wards…`}
        value={wardSearch}
        onChange={(e) => setWardSearch(e.target.value)}
        aria-label="Search wards"
      />

      {anchorDeptId
        ? (
          <>
            {renderWardGroup("This admission's department", anchorWards, 'anchor')}
            {renderWardGroup('All other wards', otherWards, 'other')}
            {anchorWards.length === 0 && otherWards.length === 0 && (
              <p className="text-sm text-slate-500 dark:text-slate-400">No wards match your search.</p>
            )}
          </>
        )
        : (
          <>
            {renderWardGroup('All wards', otherWards.concat(anchorWards), 'all')}
            {otherWards.length === 0 && anchorWards.length === 0 && (
              <p className="text-sm text-slate-500 dark:text-slate-400">No wards match your search.</p>
            )}
          </>
        )}

      {/* Bed picker */}
      {!selectedWardId ? (
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400" data-testid="bed-empty-pick-ward">
          Pick a ward to see its beds.
        </p>
      ) : (
        <div className="mt-4" data-testid="bed-list">
          <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-500 dark:text-slate-400 mb-2">
            <span><i className="inline-block w-2.5 h-2.5 rounded-sm bg-green-500 mr-1 align-middle" />Vacant — selectable</span>
            <span><i className="inline-block w-2.5 h-2.5 rounded-sm bg-red-500 mr-1 align-middle" />Occupied — unavailable</span>
            <span><i className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-500 mr-1 align-middle" />Cleaning</span>
            <span><i className="inline-block w-2.5 h-2.5 rounded-sm bg-slate-400 mr-1 align-middle" />Blocked</span>
          </div>
          <input
            className="w-full mb-3 px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            placeholder="Search bed label… (A-204)"
            value={bedSearch}
            onChange={(e) => setBedSearch(e.target.value)}
            aria-label="Search beds"
          />

          {bedsOfWard.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="bed-empty">No beds in this ward.</p>
          ) : (
            Object.entries(bedsByRoom).map(([roomId, roomBeds]) => {
              const room = roomById[roomId];
              const cat = room && room.bedcat_id ? categoriesById[room.bedcat_id] : null;
              const collapsed = collapsedRooms[roomId];
              return (
                <div className={`${cardCls} mb-2`} key={roomId}>
                  <button
                    type="button"
                    onClick={() => setCollapsedRooms((s) => ({ ...s, [roomId]: !s[roomId] }))}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 text-[12.5px] font-semibold text-slate-800 dark:text-slate-100"
                  >
                    <span>{collapsed ? '▸' : '▾'}</span>
                    <span>{room ? room.name || 'Room' : 'Room'}</span>
                    {cat && (
                      <span className="text-[11px] font-normal text-slate-500 dark:text-slate-400">
                        {cat.name} · {formatRupees(cat.per_day_charge)}/day
                      </span>
                    )}
                  </button>
                  {!collapsed && (
                    <div className="flex flex-wrap gap-2 p-3">
                      {roomBeds.map((bed) => {
                        const status = statusOf(bed.bed_id);
                        const selectable = isBedSelectable(status);
                        const selected = value === bed.bed_id;
                        const isOwnBed = !!currentBedId && bed.bed_id === currentBedId;
                        const occupant = !selectable ? occupantOf(bed.bed_id) : null;
                        const toneMap = {
                          [BED_STATES.OCCUPIED]: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
                          [BED_STATES.CLEANING]: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
                          [BED_STATES.BLOCKED]: 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
                          [BED_STATES.RESERVED]: 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
                        };
                        return (
                          <button
                            key={bed.bed_id}
                            type="button"
                            disabled={!selectable || disabled}
                            aria-disabled={!selectable}
                            data-testid={`bed-${bed.bed_id}`}
                            data-status={status}
                            onClick={() => selectBed(bed)}
                            className={`w-[104px] px-2.5 py-2 rounded-lg border text-center ${
                              selected
                                ? 'bg-teal-600 border-teal-600 text-white'
                                : selectable
                                  ? 'bg-white dark:bg-slate-800 border-green-400 dark:border-green-700 text-slate-800 dark:text-slate-100 hover:border-teal-500'
                                  : isOwnBed
                                    ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-400 dark:border-blue-600 cursor-not-allowed opacity-90'
                                    : 'bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 cursor-not-allowed opacity-80'
                            }`}
                          >
                            <span className="block text-[12.5px] font-semibold">{bed.label}</span>
                            <span className={`text-[10px] font-bold uppercase ${selected ? 'text-teal-100' : selectable ? 'text-green-600 dark:text-green-400' : ''}`}>
                              {selected ? 'Selected ✓' : (
                                selectable
                                  ? BED_STATE_LABELS.VACANT
                                  : isOwnBed
                                    ? chip('Current bed', 'bg-blue-100 dark:bg-blue-800/60 text-blue-700 dark:text-blue-200')
                                    : chip(BED_STATE_LABELS[status] || status, toneMap[status] || 'bg-slate-200 text-slate-600')
                              )}
                            </span>
                            {/* Round-2 B1 — WHO holds this bed, so the desk never blindly
                                tries to move someone onto an occupied bed. The admission's
                                own current bed is never re-labelled "Occupied by …" — it
                                already reads "Current bed" above. */}
                            {!isOwnBed && occupant && (occupant.patient_name || occupant.ip_number) && (
                              <span
                                className="block mt-0.5 text-[10px] leading-tight font-medium text-slate-700 dark:text-slate-300 break-words"
                                data-testid={`bed-occupant-${bed.bed_id}`}
                              >
                                Occupied by {occupant.patient_name || 'patient'}
                                {occupant.ip_number ? ` · ${occupant.ip_number}` : ''}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// Re-export so a caller (e.g. desk after a 409) can force a bed-availability
// refresh through the same single-flight path.
export { useBedStates };
